import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';
import {Node, useReactFlow} from 'reactflow';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import TaskSchemaNodeRenderer, {
  TaskSchemaNodeData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';

import {SchemaInfo} from '../FieldMappingConstants';
import {generateTaskSchemaNodeIdentifier} from './flowchartIdentifierUtils';
import {
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  FLOWCHART_TASK_SECONDARY_INFO,
  ProcessFlowchartNodeType,
} from './transitionModalWithFlowchartEditorConstants';
import {
  FlowchartOptions,
  createNode,
  createStaticNodeData,
  isFlowchartTaskSchema,
} from './transitionModalWithFlowchartEditorUtils';

const DEFAULT_NODE_PROPS = {
  selected: false,
  isConnectable: false,
  xPos: 0,
  yPos: 0,
  zIndex: 0,
};

/**
 * Creates a node specifically for the sidepanel.
 * This node is draggable onto the canvas, but does not have sections or field / header interactions.
 */
function createNodeDataForSidepanel(schemaInfo: SchemaInfo, nodeId: string): TaskSchemaNodeData {
  return {
    id: nodeId,
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeName: schemaInfo.taskSchema.name,
    nodeSecondaryInfo: isFlowchartTaskSchema(schemaInfo)
      ? schemaInfo.itemType === ModelTypeConstants.PROCESS_TASK
        ? FLOWCHART_TASK_SECONDARY_INFO
        : FLOWCHART_OUTPUT_SECONDARY_INFO
      : undefined,
    sections: [],
    hideSectionSummaries: true,
    onNodeHeaderClick: noop,
    onFieldClick: noop,
    nodeDetailsSidepanelContent: () => null,
    rowRenderer: () => null,
    nodeHeaderHandleRenderer: () => null,
  };
}

// Offset to ensure that nodes are dropped in the correct location on the canvas, determined via trial and error
const DROP_X_OFFSET = -70;
const DROP_Y_OFFSET = 85;

function DraggableTaskNode(props: {
  schemaInfo: SchemaInfo;
  nodeId: string;
  addNode: (node: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
}): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const reactFlowInstance = useReactFlow();

  const dragStartHandler = useCallback(() => {
    setIsDragging(true);
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const target = event.target as HTMLDivElement | null;
      const boundingRect = target!.getBoundingClientRect();
      const zoomScale = reactFlowInstance.getZoom();
      // When reactflow is upgraded, we can use screenToFlowPosition: https://reactflow.dev/whats-new/2023-11-10#rename-usereactflowproject-to-usereactflowscreentoflowposition
      const position = reactFlowInstance.project({
        x: event.clientX - boundingRect.left + DROP_X_OFFSET * zoomScale,
        y: event.clientY - boundingRect.top + DROP_Y_OFFSET * zoomScale,
      });

      const staticNodeData = createStaticNodeData(props.schemaInfo);
      const newNode = createNode(staticNodeData, position, props.options);

      props.addNode(newNode);
    },
    [props, reactFlowInstance]
  );

  const dragEndHandler = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      setIsDragging(false);
      onDrop(event);
    },
    [onDrop]
  );

  const nodeData = useMemo(
    () => createNodeDataForSidepanel(props.schemaInfo, props.nodeId),
    [props.schemaInfo, props.nodeId]
  );

  return (
    <div
      className={css(isDragging ? styles.draggingNode : styles.draggableNode)}
      draggable={true}
      onDragStart={dragStartHandler}
      onDragEnd={dragEndHandler}
    >
      <TaskSchemaNodeRenderer
        {...DEFAULT_NODE_PROPS}
        id={props.nodeId}
        type={ProcessFlowchartNodeType.TASK_SCHEMA_NODE}
        data={nodeData}
        dragging={isDragging}
      />
    </div>
  );
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function AddNodeSidepanelContent(props: {
  nodeSchemaInfos: Array<SchemaInfo>;
  addNodeToCanvas: (newNode: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
}): JSX.Element {
  return (
    <div className={css(styles.taskNodesContainer)}>
      {props.nodeSchemaInfos.map((schemaInfo) => {
        const nodeId = generateTaskSchemaNodeIdentifier(schemaInfo);
        return (
          <DraggableTaskNode
            key={nodeId}
            nodeId={nodeId}
            schemaInfo={schemaInfo}
            addNode={props.addNodeToCanvas}
            options={props.options}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  taskNodesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  draggableNode: {cursor: 'grab', margin: 6},
  draggingNode: {cursor: 'grabbing', margin: 6},
});
